import { useState, useEffect, useMemo, useCallback } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useSelector } from 'react-redux';
import { UsersForm } from '../components/UsersForm';
import { DataTable } from '../components/DataTable';
import { DateCell } from '../components/DateCell';
import { UserDeleteOwnedResourcesDialog } from '../components/UserDeleteOwnedResourcesDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { User, UserPayload, UserDeletionImpact, DeleteUserApiPayload } from '../features/users/types';
import { fetchUserDeletionImpact } from '../features/users/usersApi';
import {
  loadUsers,
  addUser,
  editUser,
  removeUser,
  setUsersSearch,
} from '../features/users/usersSlice';
import { loadRoles } from '../features/roles/rolesSlice';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { createColumnHelper } from '@tanstack/react-table';

const columnHelper = createColumnHelper<User>();

const columns = [
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('role', {
    header: 'Rol',
    cell: (info) => {
      const role = info.getValue();
      if (role === 'superadmin') return 'Superadministrador';
      if (role === 'admin') return 'Administrador';
      return role;
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Creado',
    cell: (info) => <DateCell value={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Acciones',
    cell: () => (
      <div className="flex gap-2">
        <button className="btn btn-secondary btn-sm">Editar</button>
        <button className="btn btn-danger btn-sm">Eliminar</button>
      </div>
    ),
  }),
];

function UserActionsCell({ user, onEdit, onDelete }: {
  user: User;
  onEdit: (item: User) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
        onClick={() => onEdit(user)}
      >
        Editar
      </button>
      {user.role !== 'superadmin' && (
        <button
          className="inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
          onClick={() => onDelete(user.id)}
        >
          Eliminar
        </button>
      )}
    </div>
  );
}

export function UsersPage() {
  const { confirm } = useConfirmDialog();
  const dispatch = useAppDispatch();
  const {
    items,
    loading,
    error,
    pagination,
    search
  } = useSelector((state: RootState) => state.users);

  const { items: roles, loading: rolesLoading } = useSelector((state: RootState) => state.roles);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<User | null>(null);
  const [formError, setFormError] = useState<string>('');
  const [userActionError, setUserActionError] = useState<string>('');
  const [deleteOwnedDialog, setDeleteOwnedDialog] = useState<{
    id: number;
    email: string;
    impact: UserDeletionImpact;
  } | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteModalError, setDeleteModalError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(loadRoles({ pageSize: 200 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadUsers({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search
    }));
  }, [dispatch, pagination.page, pagination.pageSize, search]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingItem(null);
    setFormError('');
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormError('');
    setFormOpen(true);
  };

  const handleSubmit = async (values: UserPayload) => {
    setFormError('');
    if (editingItem) {
      const result = await dispatch(editUser({ id: editingItem.id, payload: values }));
      if (editUser.fulfilled.match(result)) {
        closeForm();
      } else {
        const errorMessage = result.error?.message || 'Error al actualizar el usuario';
        setFormError(errorMessage);
      }
    } else {
      const result = await dispatch(addUser(values));
      if (addUser.fulfilled.match(result)) {
        closeForm();
      } else {
        const errorMessage = result.error?.message || 'Error al crear el usuario';
        setFormError(errorMessage);
      }
    }
  };

  const handleEdit = useCallback((item: User) => {
    setEditingItem(item);
    setFormError('');
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: number) => {
      const target = items.find((u) => u.id === id);
      if (!target) return;
      setUserActionError('');
      setDeleteModalError(null);
      try {
        const impact = await fetchUserDeletionImpact(id);
        const total = impact.assessmentCount + impact.communicationMatrixCount;
        if (total === 0) {
          const ok = await confirm({
            title: 'Eliminar usuario',
            message: '¿Está seguro de que desea eliminar este usuario?',
            confirmText: 'Eliminar',
            variant: 'danger',
          });
          if (ok) {
            const result = await dispatch(removeUser({ id }));
            if (removeUser.rejected.match(result) && result.payload) {
              setUserActionError(result.payload);
            }
          }
          return;
        }
        setDeleteOwnedDialog({ id, email: target.email, impact });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'No se pudo verificar los datos del usuario';
        setUserActionError(msg);
      }
    },
    [confirm, items, dispatch],
  );

  const handleConfirmDeleteWithOwned = useCallback(
    async (payload: DeleteUserApiPayload) => {
      if (!deleteOwnedDialog) return;
      setDeleteSubmitting(true);
      setDeleteModalError(null);
      try {
        const result = await dispatch(
          removeUser({ id: deleteOwnedDialog.id, payload }),
        );
        if (removeUser.fulfilled.match(result)) {
          setDeleteOwnedDialog(null);
          setUserActionError('');
        } else if (removeUser.rejected.match(result)) {
          setDeleteModalError(result.payload ?? 'No se pudo eliminar el usuario');
        }
      } finally {
        setDeleteSubmitting(false);
      }
    },
    [deleteOwnedDialog, dispatch],
  );

  const actionsCellRenderer = useCallback(
    (info: any) => (
      <UserActionsCell user={info.row.original} onEdit={handleEdit} onDelete={handleDelete} />
    ),
    [handleEdit, handleDelete],
  );

  const tableColumns = useMemo(() => columns.map(col => ({
    ...col,
    cell: col.id === 'actions' ? actionsCellRenderer : col.cell,
  })), [actionsCellRenderer]);

  const roleOptions = ['superadmin', 'admin', ...roles.map(role => role.name)];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Usuarios</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          Nuevo Usuario
        </button>
      </div>

      <div>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-foreground mb-1">Buscar usuarios</label>
          <input
            type="text"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
            placeholder="Buscar por email..."
            value={search}
            onChange={(e) => dispatch(setUsersSearch(e.target.value))}
          />
        </div>
      </div>

      <div>
        {(error || userActionError) && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
            {userActionError || error}
          </div>
        )}
        <DataTable
          columns={tableColumns}
          data={items}
          loading={loading}
          emptyMessage="No hay usuarios registrados"
          pageSizeOptions={[10, 20, 50]}
        />
      </div>

      <Dialog open={formOpen} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          </DialogHeader>
          {rolesLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Cargando roles…</div>
          ) : (
            <UsersForm
              initialValues={editingItem}
              onSubmit={handleSubmit}
              onCancel={closeForm}
              error={formError}
              onEmailChange={(value) => {
                if (value.length > 255) {
                  setFormError('El email no puede exceder 255 caracteres');
                } else {
                  setFormError('');
                }
              }}
              roleOptions={roleOptions}
            />
          )}
        </DialogContent>
      </Dialog>

      {deleteOwnedDialog && (
        <UserDeleteOwnedResourcesDialog
          open
          userId={deleteOwnedDialog.id}
          userEmail={deleteOwnedDialog.email}
          impact={deleteOwnedDialog.impact}
          userOptions={items.map((u) => ({ id: u.id, email: u.email }))}
          submitting={deleteSubmitting}
          errorMessage={deleteModalError}
          onClose={() => !deleteSubmitting && setDeleteOwnedDialog(null)}
          onConfirm={handleConfirmDeleteWithOwned}
        />
      )}
    </div>
  );
}
